import React from "react";
import NavBar from "../component/NavBar";
import Footer from "../component/Footer";
import Phone from "../component/Phone";
import { Bipolardisorder } from "assets";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBalanceScale,
  faCheckCircle,
  faPills,
  faUserDoctor,
} from "@fortawesome/free-solid-svg-icons";

const bipolarSymptoms = [
  "Extreme mood swings (highs and lows)",
  "Changes in energy and activity levels",
  "Impulsive or risky behavior during manic episodes",
  "Depressive episodes with sadness and hopelessness",
  "Difficulty maintaining relationships",
  "Sleep pattern disruptions",
];

const treatmentComponents = [
  {
    icon: faBalanceScale,
    title: "Mood Stabilization",
    description: "Comprehensive strategies to balance emotional extremes",
  },
  {
    icon: faPills,
    title: "Medication Management",
    description: "Carefully monitored pharmaceutical interventions for symptom control",
  },
  {
    icon: faUserDoctor,
    title: "Therapeutic Support",
    description: "Ongoing counseling to navigate life's challenges effectively",
  },
];

const BipolarDisorder = () => {
  return (
    <div className="bg-white">
      <NavBar />
      <Phone />

      <div className="relative h-[400px] md:h-[500px] lg:h-[600px]">
        <img
          className="w-full h-full object-cover"
          src={Bipolardisorder}
          alt="Bipolar Disorder Treatment"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        <div className="absolute inset-0 flex items-center justify-center px-6">
          <div className="text-center animate-fade-in">
            <div className="inline-block px-4 py-2 bg-primary-500/90 rounded-full mb-4">
              <span className="text-white font-semibold text-body-sm">Mental Health Service</span>
            </div>
            <h1 className="text-display-sm md:text-display-md lg:text-display-lg text-white font-bold mb-4">
              Bipolar Disorder Treatment
            </h1>
            <p className="text-body-lg md:text-heading-sm text-white/90 max-w-2xl mx-auto">
              Expert care for managing mood fluctuations and achieving stability
            </p>
          </div>
        </div>
      </div>

      <div className="py-16 md:py-20 lg:py-24 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-heading-xl md:text-display-sm text-neutral-900 mb-6">
              Understanding Bipolar Disorder
            </h2>
            <div className="h-1 w-24 bg-primary-500 mx-auto mb-8"></div>
          </div>

          <div className="bg-white rounded-xl shadow-medium p-8 md:p-10">
            <p className="text-body-lg text-neutral-700 leading-relaxed mb-6">
              Bipolar Disorder is characterized by significant shifts in energy levels, moods, behaviors, and other complex dimensions of daily life. These fluctuations can substantially impact well-being and interfere with optimal functioning, affecting overall quality of life.
            </p>
            <p className="text-body-lg text-neutral-700 leading-relaxed">
              Our comprehensive treatment approach combines psychotherapy and medication management. Psychotherapy provides a structured environment to process experiences and address challenges, while medication helps regulate brain chemistry and stabilize mood swings, temperament variations, and energy levels. Together, these interventions create a powerful foundation for managing the condition effectively.
            </p>
          </div>
        </div>
      </div>

      <div className="py-16 md:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-heading-xl md:text-display-sm text-neutral-900 mb-4">
              Common Bipolar Disorder Symptoms
            </h2>
            <p className="text-body-lg text-neutral-600">
              Recognizing the patterns can lead to better management
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bipolarSymptoms.map((symptom, index) => (
              <div
                key={index}
                className="bg-neutral-50 rounded-xl p-6 hover:bg-primary-50 transition-colors duration-300 group"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="text-primary-500 text-xl group-hover:text-primary-600 transition-colors"
                    />
                  </div>
                  <p className="text-body-md text-neutral-800 font-medium">
                    {symptom}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="py-16 md:py-20 lg:py-24 bg-gradient-to-b from-primary-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-heading-xl md:text-display-sm text-neutral-900 mb-4">
              Comprehensive Treatment Approach
            </h2>
            <p className="text-body-lg text-neutral-600 max-w-2xl mx-auto">
              Integrating medication and therapy for optimal outcomes
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {treatmentComponents.map((component, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-soft hover:shadow-medium transition-all duration-300 p-8 text-center group"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-primary-500 transition-colors">
                  <FontAwesomeIcon
                    icon={component.icon}
                    className="text-2xl text-primary-600 group-hover:text-white transition-colors"
                  />
                </div>
                <h3 className="text-heading-sm text-neutral-900 mb-3">
                  {component.title}
                </h3>
                <p className="text-body-md text-neutral-600 leading-relaxed">
                  {component.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="py-16 md:py-20 lg:py-24 bg-gradient-to-br from-primary-500 to-primary-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-heading-xl md:text-display-sm text-white mb-6">
            Achieve Balance and Stability
          </h2>
          <p className="text-body-lg text-white/90 mb-8 leading-relaxed">
            With proper treatment and support, you can manage bipolar disorder effectively and live a fulfilling life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="px-8 py-4 bg-white text-primary-600 font-semibold rounded-lg hover:bg-neutral-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                Begin Treatment
              </button>
            </Link>
            <Link to="/mainservicepage">
              <button className="px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-300">
                Explore Services
              </button>
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BipolarDisorder;
